import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { getBranch } from "../../utils/app.utils";

import DepositRefundApi, {
  DepositRefundParams,
  DepositRefundSide,
  DepositRefundStallOption,
  DepositRefundStatus,
  DepositRefundZoneOption,
} from "../../api/deposit-refund";
import {
  DEPOSIT_REFUND_FILTER_ALL,
  DEPOSIT_REFUND_STATUS_ALL,
} from "../../constants/depositRefund";
import { stallLabel as formatStallLabel } from "../../utils/stall.utils";

/** ค่าของ dropdown ที่ยังไม่กรอง — sentinel ที่ไม่ใช่ '' เพราะ MUI Select ไม่เรียก renderValue ตอนค่าว่าง */
type FilterValue = number | typeof DEPOSIT_REFUND_FILTER_ALL;

/**
 * state ของแถบตัวกรอง + ตัวเลือกโซน/แผงค้า ใช้ร่วมกันสองหน้า (เจ้าหน้าที่ / ลูกค้า)
 *
 * ตั้งใจให้ hook นี้ "ไม่ยิง API ของตาราง" — คืนแค่ state กับ params ที่ประกอบแล้ว
 * ให้แต่ละหน้าเขียน effect โหลดข้อมูลเอง เพื่อให้เห็นชัดว่าหน้านั้นเรียก endpoint ไหน
 *
 * เหตุที่รวม state ไว้ที่เดียว: กฎ "เปลี่ยนตัวกรองแล้วต้องกลับไปหน้า 1" ถ้าเขียนแยกสองหน้า
 * จะหลุดในสำเนาใดสำเนาหนึ่งแน่นอน แล้วอาการคือกรองแล้วตารางว่างเพราะยังค้างอยู่หน้า 2
 */
export const useDepositRefundFilter = (side: DepositRefundSide) => {
  /**
   * สาขาที่เลือกไว้ตอน login — ฝั่งเจ้าหน้าที่ต้องส่งทุกเส้น ไม่งั้นเห็นคำร้องข้ามสาขา
   * (backend ไม่ได้ scope จาก token เพราะ user 1 คนไม่ผูกกับสาขาเดียว spec ข้อ 1.4)
   * ฝั่งลูกค้าไม่ต้องส่ง scope ด้วย userId จาก token อยู่แล้ว
   */
  const branchId = useMemo(() => {
    if (side === "CUSTOMER") return undefined;
    const value = Number(getBranch());
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }, [side]);

  /** ค่าที่พิมพ์อยู่ในช่อง — แยกจาก search เพื่อ debounce ไม่ให้ยิง API ทุก keystroke */
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>(DEPOSIT_REFUND_STATUS_ALL);
  const [zoneId, setZoneId] = useState<FilterValue>(DEPOSIT_REFUND_FILTER_ALL);
  const [stallId, setStallId] = useState<FilterValue>(DEPOSIT_REFUND_FILTER_ALL);
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);

  const [zones, setZones] = useState<DepositRefundZoneOption[]>([]);
  const [stalls, setStalls] = useState<DepositRefundStallOption[]>([]);

  useEffect(() => {
    let isMounted = true;

    const loadFilterMeta = async () => {
      try {
        // เป็น 2 endpoint แยกกันตาม spec ข้อ 3.3 ยิงขนานได้ ไม่ต้องรอกัน
        const [zoneRes, stallRes] = await Promise.all([
          DepositRefundApi.filterZone(side, branchId),
          DepositRefundApi.filterStall(side, branchId),
        ]);
        if (!isMounted) return;
        setZones(zoneRes.data?.data || []);
        setStalls(stallRes.data?.data || []);
      } catch (error) {
        // ตัวเลือกในตัวกรองไม่ใช่ข้อมูลหลัก พลาดแล้วให้เงียบ ไม่รบกวนซ้ำกับ error ของตาราง
        if (!isMounted) return;
        setZones([]);
        setStalls([]);
      }
    };

    loadFilterMeta();
    return () => {
      isMounted = false;
    };
  }, [side, branchId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleChangeStatus = (value: string) => {
    setStatus(value);
    setPage(1);
  };

  /**
   * แผงค้าที่อยู่ในโซนที่เลือก — option ของ /filter/stall มี zoneId มาด้วยแล้ว (spec v2)
   * และเป็น zoneId ตัวเดียวกับที่ใช้กรอง ?zoneId= จึงไม่มีเคสที่กรองแล้วตารางว่าง
   */
  const stallOptions = useMemo(() => {
    if (zoneId === DEPOSIT_REFUND_FILTER_ALL) return stalls;
    return stalls.filter((stall) => stall.zoneId === zoneId);
  }, [stalls, zoneId]);

  /** เปลี่ยนโซนต้องล้างแผงค้าเดิม ไม่งั้นค้างแผงของโซนอื่นแล้วตารางว่างแบบหาสาเหตุไม่เจอ */
  const handleChangeZone = (value: FilterValue) => {
    setZoneId(value);
    setStallId(DEPOSIT_REFUND_FILTER_ALL);
    setPage(1);
  };

  const handleChangeStall = (value: FilterValue) => {
    setStallId(value);
    setPage(1);
  };

  const handleChangeDateStart = (value: any) => {
    // InputDatePicker โชว์เป็น พ.ศ. แต่ค่าที่ส่ง backend ต้องเป็น ค.ศ. ISO
    setDateStart(value ? moment(value).format("YYYY-MM-DD") : "");
    setPage(1);
  };

  const handleChangeDateEnd = (value: any) => {
    setDateEnd(value ? moment(value).format("YYYY-MM-DD") : "");
    setPage(1);
  };

  const handleChangePageLimit = (value: number) => {
    setPageLimit(value);
    setPage(1);
  };

  /**
   * ตัวกรองที่ใช้ร่วมกันระหว่างตารางกับ summary
   * ไม่รวม status/page/limit เพื่อให้ summary นับได้ครบทุกสถานะ
   */
  const buildBaseParams = useCallback((): DepositRefundParams => {
    let condition: DepositRefundParams = {};
    if (branchId) condition = { ...condition, branchId };
    if (search) condition = { ...condition, keyword: search };
    if (zoneId !== DEPOSIT_REFUND_FILTER_ALL) condition = { ...condition, zoneId };
    if (stallId !== DEPOSIT_REFUND_FILTER_ALL) condition = { ...condition, stallId };
    if (dateStart) condition = { ...condition, date_from: dateStart };
    if (dateEnd) condition = { ...condition, date_to: dateEnd };
    return condition;
  }, [branchId, search, zoneId, stallId, dateStart, dateEnd]);

  /** params เต็มของตาราง = ตัวกรองร่วม + สถานะ + หน้า */
  const buildTableParams = useCallback((): DepositRefundParams => {
    let condition: DepositRefundParams = { ...buildBaseParams(), page, limit: pageLimit };
    if (status !== DEPOSIT_REFUND_STATUS_ALL) {
      condition = { ...condition, status: status as DepositRefundStatus };
    }
    return condition;
  }, [buildBaseParams, page, pageLimit, status]);

  /** ข้อความในหัว dropdown — โซน/แผงค้าใช้ชื่อจาก option ที่เลือก */
  const zoneLabel =
    zoneId === DEPOSIT_REFUND_FILTER_ALL
      ? "ทั้งหมด"
      : zones.find((zone) => zone.zoneId === zoneId)?.zone_name || "ทั้งหมด";

  const stallLabel = (() => {
    if (stallId === DEPOSIT_REFUND_FILTER_ALL) return "ทั้งหมด";
    const stall = stallOptions.find((item) => item.stallId === stallId);
    if (!stall) return "ทั้งหมด";
    return stall.stall_number || stall.stall_name || "ทั้งหมด";
  })();

  /** ข้อความในลิสต์ตัวเลือกแผงค้า — ชื่อร้าน + เลขแผง ให้แยกแผงที่ชื่อซ้ำกันได้ */
  const stallOptionLabel = (stall: DepositRefundStallOption) => formatStallLabel(stall);

  return {
    searchInput,
    setSearchInput,
    status,
    handleChangeStatus,
    zoneId,
    handleChangeZone,
    zoneLabel,
    stallId,
    handleChangeStall,
    stallLabel,
    stallOptionLabel,
    dateStart,
    handleChangeDateStart,
    dateEnd,
    handleChangeDateEnd,
    page,
    setPage,
    pageLimit,
    handleChangePageLimit,
    zones,
    /** ลิสต์ที่กรองตามโซนที่เลือกแล้ว — หน้าเพจใช้ตัวนี้ทำ option ไม่ใช่ stalls ดิบ */
    stallOptions,
    buildBaseParams,
    buildTableParams,
  };
};
